import React, { useEffect, useRef, useContext, useState, useMemo, useCallback } from 'react';
import { PatientDataContext } from '../../../context/PatientDataContext';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../../../components/Layout';
import Breadcrumb from '../../../components/Breadcrumb';
import PADSideNavbar from './components/PADSideNavbar';
import { ChevronLeftIcon, CheckIcon, PlusIcon, CheckCircleIcon } from '@heroicons/react/solid';
import { SearchIcon, XIcon } from '@heroicons/react/outline';
import * as Dialog from '@radix-ui/react-dialog';
import { FormattedAnswer, normalizeRawAnswer } from '../../../utils/formDisplayUtils';
import InfoTooltip from '../../../components/InfoTooltip';
import { useCanEditStage } from '../../../hooks/useCanEditStage';
import { useAuth } from '../../../hooks/useAuth';
import useWorkflowConfig from '../../../hooks/useWorkflowConfig';
import { getDifferentialDiagnoses } from '../../../utils/diagnosticDecisionRouting';
import { isPadAdhdOutcomePositive, getPatientSexAtBirth } from '../../../utils/conditionChecks';
import DictationTextArea from '../../../components/DictationTextArea';
import ButtonSpinner from '../../../components/informantManagement/ButtonSpinner';
import { TriggerGenerateDocumentSection } from '../../../utils/TriggerGenerateDocumentSection';
import { useSectionExitRegeneration } from '../../../hooks/useSectionExitRegeneration';
import { auth } from '../../../firebase';
import { serverTimestamp } from 'firebase/firestore';
import { buildFirstCompletedFields, buildFirstGeneratedFields, buildPadCompletionFields, appendReportAuditEntry } from '../../../utils/reportAuditHelpers';
import { recordInferredAppointmentDateOnCompletion } from '../../../utils/appointmentDateHelpers';
import ModuleCompletionDialog, { CancelButton, GenerateReportButton, SummarySection } from '../../../components/ModuleCompletionDialog';
import GenerateReportProgressDialog from '../../../components/GenerateReportProgressDialog';
import PatientContractTypeBadge from '../../../components/PatientContractTypeBadge';

/**
 * PAD Report Decisions — The Owl Centre only.
 *
 * This page replaces the standard DIS/TRT flow for TOC patients.
 * It presents the clinician with a concise set of Yes/No decisions
 * whose answers directly control conditional content blocks in the
 * TOC Master Patient Report template.
 *
 * Decisions are persisted at:
 *   dataModel.5_postAssessmentDiscussion.sections.6
 *
 * The report rendering Cloud Function (renderPatientReportTemplateTOC)
 * reads these values to determine which conditional blocks to include/
 * exclude from the final patient report.
 */
export default function PADReportDecisionsTOC() {
  const { patientId } = useParams();
  const navigate = useNavigate();
  const { patientDataModel, updatePatientFirestore, flushPendingUpdates } = useContext(PatientDataContext);
  const { claims } = useAuth();
  const wf = useWorkflowConfig(patientDataModel, claims);
  const { usesNextStepsPad, isAppointmentDatesEnabled = false, skipDifferentialsDecisions } = wf;
  const [hasChanges, setHasChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isNavigating, setIsNavigating] = useState(false);

  // Free-text rationale for declining a Workplace Needs Assessment (Lexxic only).
  // Persisted at dataModel.5_postAssessmentDiscussion.sections.6.wnaDeclinedRationale
  // and used as LLM input to generate a patient-facing paragraph for the report.
  const [wnaDeclinedRationale, setWnaDeclinedRationale] = useState('');

  const canEdit = useCanEditStage(patientDataModel, 'PAD');

  // Navigation constants — declared early so useSectionExitRegeneration can
  // reference them. The hook registers an unmount-based safety net that fires
  // TriggerGenerateDocumentSection if the user leaves via an unmanaged exit
  // (breadcrumb, top nav, browser back). markRegenHandled() is called in
  // handleSectionCompletion to prevent double-firing on managed exits.
  const currentStageTitle = 'Post-Assessment Discussion';
  const sectionKey = '6';              // Firestore path: 5_postAssessmentDiscussion.sections.6
  const displayNumber = 2;             // UI numbering (2nd page TOC clinicians see)
  const currentSectionTitle = 'Next Steps';
  const sectionType = 'toc_next_steps';

  // The patient's contract type determines which conditional questions appear.
  // Stored at customFields.assessmentType (labelled "Contract Type" in the UI).
  const contractType = patientDataModel?.customFields?.assessmentType || '';
  // Workplace Needs Assessment is currently only offered on Lexxic contracts,
  // but the variable name is kept contract-agnostic so additional contracts
  // can be added here without renaming anything downstream.
  const isWorkplaceNeedsAssessmentContract = contractType === 'Lexxic Ltd';

  // Whether the patient has a positive ADHD diagnosis. Declared before the
  // redirect guard useEffect below so it is in scope when the effect runs.
  const isDiagnosisPositive = useMemo(
    () => isPadAdhdOutcomePositive(patientDataModel),
    [patientDataModel]
  );

  // Whether autism is listed as an emerging differential for this patient.
  // Drives the "Include autism follow-up instructions" toggle — only active
  // when autism is actually under consideration. Uses the same check as the
  // backend _compute_autism_referral_key (case-insensitive name match).
  const isAutismEmergingDifferential = useMemo(() => {
    const differentials = getDifferentialDiagnoses(patientDataModel);
    return differentials.some((name) => name.toLowerCase().includes('autism'));
  }, [patientDataModel]);

  // Whether the "Include autism follow-up instructions" panel should be
  // active and interactable. Only relevant when differentials decisions are
  // disabled (TOC) because the old per-condition autism referral logic in the
  // DD module is no longer available.
  const isAutismFollowUpActive = skipDifferentialsDecisions && isAutismEmergingDifferential;

  // Redirect guard: this page is only accessible for patients whose PAD
  // structure is "Next Steps" (i.e. usesNextStepsPad) with a positive ADHD
  // diagnosis. Non-Next-Steps patients are sent to the standard DIS page;
  // Next Steps patients with a negative diagnosis are sent back to
  // Assessment Outcome (PAD ends there for them). Both checks are safe to
  // evaluate once patientDataModel has loaded.
  useEffect(() => {
    if (!patientDataModel) return;
    if (!usesNextStepsPad) {
      navigate(
        `/patient/${patientId}/proforma/post-assessment-discussion/strategies-and-recommendations`,
        { replace: true }
      );
    } else if (!isDiagnosisPositive) {
      navigate(
        `/patient/${patientId}/proforma/post-assessment-discussion/review-assessment`,
        { replace: true }
      );
    }
  }, [patientDataModel, usesNextStepsPad, isDiagnosisPositive, navigate, patientId]);

  // Reads the patient's sex assigned at birth from their intake form.
  // Shown as informational text next to the "Females with ADHD" question
  // so clinicians can make an informed choice without a pre-selection.
  const patientSexAtBirth = useMemo(
    () => getPatientSexAtBirth(patientDataModel),
    [patientDataModel]
  );

  // Controls the magnifying-glass dialog on the "Females with ADHD" question.
  // When open, displays the patient's original sex-at-birth form answer so the
  // clinician can verify the source data without leaving the page — same UX
  // pattern as the goals panel magnifying glass in 1_REV.js.
  const [isSexDialogOpen, setIsSexDialogOpen] = useState(false);

  // Extracts the sex-at-birth question's raw form data from the patient's
  // intake form ("Your Information" section) for the magnifying-glass dialog.
  // Returns an array of items in the same shape as getRawPrefillInputs in
  // 1_REV.js / CIQuestionPanel.js so the dialog markup can be identical.
  const sexAtBirthRawPrefillData = useMemo(() => {
    const yourInfo = patientDataModel?.formData?.yourInformation;
    if (!yourInfo?.formSubsections) return [];

    for (const sub of Object.values(yourInfo.formSubsections)) {
      for (const q of Object.values(sub.questions || {})) {
        if (q.questionVarName === 'sexAssignedAtBirth') {
          const sq = q.subquestions
            ? Object.values(q.subquestions).find(s => s.serviceUserVarName === 'sexAssignedAtBirth')
            : null;

          const rawAnswer = sq?.answer ? normalizeRawAnswer(sq.answer) : null;
          const displayAnswer = sq?.answer?.value || null;

          return [{
            formName: 'Your Information',
            questionTitle: q.questionTitle || 'Sex assigned at birth',
            question: sq?.subquestionDisplayText || 'At birth, what was your recorded sex?',
            answer: displayAnswer || 'No answer given',
            rawAnswer,
            hasAnswer: !!displayAnswer,
            options: sq?.options || [],
          }];
        }
      }
    }
    return [];
  }, [patientDataModel?.formData?.yourInformation]);

  const [decisions, setDecisions] = useState({
    wantsMedication: null,
    benefitsFromWorkplaceAssessment: null,
    includeAutismFollowUp: null,
    includeDisclosureWork: null,
    includeDisclosureEducation: null,
    includeDisclosureFriendsFamily: null,
    includeDriving: null,
    includeRelationships: null,
    includeFemalesWithADHD: null,
    includeExerciseDiet: null,
    includeEducationalAdvice: null,
    includeEmploymentAdvice: null,
    includeCareNeedsAssessment: null,
  });

  // One-shot seed from Firestore — only runs once when data first arrives.
  // After initial load, local state is the single source of truth, writes go
  // to Firestore via updatePatientFirestore, and we never re-read back into
  // state. This prevents the optimistic → context-update → re-sync flicker
  // that occurs when useEffect depends on patientDataModel.
  const isInitialLoad = useRef(true);
  useEffect(() => {
    if (!isInitialLoad.current) return;
    const saved = patientDataModel?.dataModel?.['5_postAssessmentDiscussion']?.sections?.[sectionKey];
    if (!saved && !patientDataModel) return;

    isInitialLoad.current = false;
    const s = saved || {};
    setDecisions({
      wantsMedication: s.wantsMedication ?? null,
      benefitsFromWorkplaceAssessment: s.benefitsFromWorkplaceAssessment ?? null,
      includeAutismFollowUp: s.includeAutismFollowUp ?? null,
      includeDisclosureWork: s.includeDisclosureWork ?? null,
      includeDisclosureEducation: s.includeDisclosureEducation ?? null,
      includeDisclosureFriendsFamily: s.includeDisclosureFriendsFamily ?? null,
      includeDriving: s.includeDriving ?? null,
      includeRelationships: s.includeRelationships ?? null,
      includeFemalesWithADHD: s.includeFemalesWithADHD ?? null,
      includeExerciseDiet: s.includeExerciseDiet ?? null,
      includeEducationalAdvice: s.includeEducationalAdvice ?? null,
      includeEmploymentAdvice: s.includeEmploymentAdvice ?? null,
      includeCareNeedsAssessment: s.includeCareNeedsAssessment ?? null,
    });
    setWnaDeclinedRationale(s.wnaDeclinedRationale || '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientDataModel]);

  // Whether there is meaningful LLM work to trigger: only when a Lexxic
  // patient has declined WNA AND typed a rationale. Used to guard both
  // the managed-exit trigger (handleSectionCompletion) and the unmount
  // safety-net hook (useSectionExitRegeneration).
  const hasWnaParagraphToGenerate = isWorkplaceNeedsAssessmentContract
    && decisions.benefitsFromWorkplaceAssessment === false
    && !!wnaDeclinedRationale.trim();

  const { markRegenHandled } = useSectionExitRegeneration({
    patientId,
    stageTitle: currentStageTitle,
    sectionTitle: currentSectionTitle,
    hasChanges,
    canEdit,
    patientDataModel,
    shouldRegenerate: hasWnaParagraphToGenerate,
  });

  // Persist the WNA declined rationale to Firestore on blur (same pattern
  // as clinician notes in 1_REV.js — onChange updates local state for
  // responsiveness, onBlur persists to avoid excessive Firestore writes).
  const handleWnaRationaleBlur = useCallback(() => {
    if (!canEdit) return;
    updatePatientFirestore(patientId, {
      '5_postAssessmentDiscussion': {
        sections: {
          [sectionKey]: {
            sectionTitle: currentSectionTitle,
            sectionType,
            wnaDeclinedRationale,
          },
        },
      }
    });
  }, [canEdit, patientId, updatePatientFirestore, wnaDeclinedRationale]);

  // Persist a decision to Firestore immediately on change
  const handleDecision = useCallback((key, value) => {
    if (!canEdit) return;

    setDecisions(prev => ({ ...prev, [key]: value }));

    updatePatientFirestore(patientId, {
      '5_postAssessmentDiscussion': {
        sections: {
          [sectionKey]: {
            sectionTitle: currentSectionTitle,
            sectionType,
            [key]: value,
          },
        },
      }
    });
  }, [canEdit, patientId, updatePatientFirestore]);

  const prevSectionLink = '/proforma/post-assessment-discussion/review-assessment';
  const nextSectionLink = '/proforma/post-assessment-discussion/strategies-and-recommendations';

  // All questions must be answered. Medication is always required since this
  // page is only rendered for positive ADHD diagnoses. Workplace Needs
  // Assessment is only required for Lexxic contract patients.
  const isFormComplete = useMemo(() => {
    if (decisions.wantsMedication === null) return false;
    if (isWorkplaceNeedsAssessmentContract && decisions.benefitsFromWorkplaceAssessment === null) return false;
    // When clinician declines WNA for a Lexxic patient, they must provide a rationale
    if (isWorkplaceNeedsAssessmentContract && decisions.benefitsFromWorkplaceAssessment === false && !wnaDeclinedRationale.trim()) return false;
    // Autism follow-up is only required when the panel is active (autism is an
    // emerging differential and differentials decisions are disabled)
    if (isAutismFollowUpActive && decisions.includeAutismFollowUp === null) return false;
    // Care needs is always shown on this page (positive ADHD only) and always
    // required. Missing/unanswered means the report omits the paragraph.
    if (decisions.includeCareNeedsAssessment === null) return false;
    if (decisions.includeDisclosureWork === null) return false;
    if (decisions.includeDisclosureEducation === null) return false;
    if (decisions.includeDisclosureFriendsFamily === null) return false;
    if (decisions.includeDriving === null) return false;
    if (decisions.includeRelationships === null) return false;
    if (decisions.includeFemalesWithADHD === null) return false;
    if (decisions.includeExerciseDiet === null) return false;
    if (decisions.includeEducationalAdvice === null) return false;
    if (decisions.includeEmploymentAdvice === null) return false;
    return true;
  }, [isWorkplaceNeedsAssessmentContract, isAutismFollowUpActive, decisions, wnaDeclinedRationale]);

  // Panel numbering is sequential (2.1, 2.2, …). Care needs is always shown.
  // When the autism follow-up panel is visible (skipDifferentialsDecisions),
  // it occupies slot 3 and care needs plus everything after it shift down.
  // When autism follow-up is hidden, care needs takes slot 3.
  const showAutismFollowUpPanel = skipDifferentialsDecisions;
  // Care needs is always present, so it occupies the slot after workplace
  // (or after autism follow-up when that panel is visible) and shifts the
  // later panels down by one.
  const panelNumbers = showAutismFollowUpPanel
    ? { medication: 1, workplace: 2, autismFollowUp: 3, careNeeds: 4, disclosure: 5, lifestyle: 6, appendix: 7 }
    : { medication: 1, workplace: 2, careNeeds: 3, disclosure: 4, lifestyle: 5, appendix: 6 };

  // Centralised exit handler — mirrors the pattern in 3_TRT.js and other PAD
  // sections.  Every navigation path out of this page (Back, Finish, sidebar)
  // routes through here so that pending writes are flushed, the section is
  // marked complete, and the WNA paragraph Cloud Function fires when needed.
  const handleSectionCompletion = useCallback(() => {
    if (!canEdit) return Promise.resolve();

    markRegenHandled();

    const currentSectionData = patientDataModel?.dataModel?.['5_postAssessmentDiscussion']?.sections?.[sectionKey];
    const isAlreadyCompleted = currentSectionData?.status === 'completed';
    const sectionsAlreadyGenerated = patientDataModel?.dataModel?.['6_manageReports']?.sections?.['4']?.stageSectionsGenerated;
    const isCurrentSectionGenerated = sectionsAlreadyGenerated?.includes(currentSectionTitle) || false;

    const needsStatusWrite = !isAlreadyCompleted;
    const needsGeneration = hasWnaParagraphToGenerate
      && (hasChanges || !isCurrentSectionGenerated);

    if (!needsStatusWrite && !needsGeneration) return Promise.resolve();

    if (needsStatusWrite) {
      updatePatientFirestore(patientId, {
        '5_postAssessmentDiscussion': {
          sections: {
            [sectionKey]: {
              sectionTitle: currentSectionTitle,
              sectionType,
              status: 'completed',
              progress: 100,
            },
          },
        }
      });
    }

    const dataReady = flushPendingUpdates({ noTimeout: true });

    if (needsGeneration) {
      TriggerGenerateDocumentSection(patientId, currentStageTitle, currentSectionTitle, [], dataReady);
    }

    return dataReady;
  }, [canEdit, patientDataModel, hasChanges, hasWnaParagraphToGenerate, patientId,
      currentStageTitle, currentSectionTitle, updatePatientFirestore, flushPendingUpdates, markRegenHandled]);

  const handleFinish = useCallback(async () => {
    if (isNavigating) return;
    document.activeElement?.blur();
    setIsSaving(true);
    try {
      await handleSectionCompletion();
      setIsDialogOpen(true);
    } catch (error) {
      console.error('Error completing Next Steps section:', error);
    } finally {
      setIsSaving(false);
    }
  }, [isNavigating, handleSectionCompletion]);

  const handleGenerateReport = useCallback(() => {
    setIsDialogOpen(false);
    setIsGenerating(true);
  }, []);

  const handleCancelGeneration = useCallback(() => {
    setIsGenerating(false);
  }, []);

  // Marks the PAD module as complete in Firestore and navigates to the
  // Documents Dashboard — identical to the post-completion step in 1_REV.js.
  const handleNextPageButtonClick = useCallback(async () => {
    const manageReports = patientDataModel?.dataModel?.['6_manageReports'];
    const firstCompletedFields = buildFirstCompletedFields(patientDataModel?.dataModel?.['5_postAssessmentDiscussion']);

    await updatePatientFirestore(patientId, {
      '5_postAssessmentDiscussion': buildPadCompletionFields(patientDataModel, firstCompletedFields),
      '6_manageReports': {
        padReportGenerated: true,
        padReportGeneratedAt: serverTimestamp(),
        padReportGeneratedBy: auth.currentUser.uid,
        padReportGeneratedByName: auth.currentUser.displayName,
        ...buildFirstGeneratedFields('pad', manageReports),
      }
    });

    appendReportAuditEntry(patientId, {
      module: 'pad',
      eventType: 'generated',
      isFirstOccurrence: !manageReports?.padReportFirstGeneratedAt,
    });

    // Inferred appointment audit on first completion (fire-and-forget; does not write appointmentDates)
    recordInferredAppointmentDateOnCompletion({
      patientId,
      moduleKey: 'pad',
      didWriteFirstCompleted: Object.keys(firstCompletedFields).length > 0,
      enabled: isAppointmentDatesEnabled,
    });

    navigate(`/patient/${patientId}/documents`);
  }, [patientId, patientDataModel, updatePatientFirestore, navigate, isAppointmentDatesEnabled]);

  const handleBack = useCallback(() => {
    if (isNavigating) return;
    document.activeElement?.blur();
    setIsNavigating(true);
    handleSectionCompletion().then(() => {
      navigate(`/patient/${patientId}${prevSectionLink}`);
    });
  }, [isNavigating, patientId, navigate, prevSectionLink, handleSectionCompletion]);

  // Side navigation sections — simplified for TOC flow
  const SECTIONS_PAD = useMemo(() => {
    return [
      { sectionTitle: 'Assessment Outcome', route: '/proforma/post-assessment-discussion/review-assessment', status: 'completed', isUnlocked: true },
      { sectionTitle: 'Next Steps', route: '/proforma/post-assessment-discussion/next-steps', status: isFormComplete ? 'completed' : 'in-progress', isUnlocked: true },
    ];
  }, [isFormComplete]);

  const navigateToSection = useCallback((index) => {
    if (isNavigating) return;
    document.activeElement?.blur();
    setIsNavigating(true);
    handleSectionCompletion().then(() => {
      const section = SECTIONS_PAD[index];
      if (section.route) {
        navigate(`/patient/${patientId}${section.route}`);
      }
    });
  }, [isNavigating, patientId, navigate, SECTIONS_PAD, handleSectionCompletion]);

  const prevSectionTitle = 'Assessment Outcome';

  const breadcrumbTuples = [
    ["Patient Overview", `/patient/${patientId}`],
    ["Post-Assessment Discussion"],
    ["Next Steps"],
  ];

  return (
    <Layout activePage="dashboard">
      <Breadcrumb tupleList={breadcrumbTuples} />
      <div className="flex">
        <PADSideNavbar
          sections={SECTIONS_PAD}
          currentSectionIndex={1}
          navigateToSection={navigateToSection}
          isNavigating={isNavigating || isSaving}
        />
        <div className="flex-grow p-8 text-left">

          {/* Navigation Buttons — top of page, matching DIS/TRT layout */}
          <div className="flex justify-between items-center mb-6">
            <button
              onClick={handleBack}
              disabled={isNavigating}
              className="flex items-center py-2 px-4 mb-2 rounded-md shadow-md text-gray-900 border border-gray-300 transition-all duration-300 hover:bg-gray-50 disabled:opacity-60 disabled:cursor-not-allowed"
              aria-label="Back to Previous Section"
            >
              <ChevronLeftIcon className="w-5 h-5 mr-2" aria-hidden="true" />
              <span className="font-bold">{isNavigating ? 'Loading...' : `Back to ${prevSectionTitle}`}</span>
            </button>

            {canEdit && (
              <button
                onClick={handleFinish}
                disabled={!isFormComplete || isSaving || isNavigating}
                className={`flex items-center py-2 px-4 mb-2 rounded-md shadow-md transition duration-100 ml-auto
                  ${(!isFormComplete || isSaving || isNavigating)
                    ? (isSaving ? 'opacity-60 cursor-not-allowed bg-violet-900 text-white' : 'bg-gray-300 text-gray-500 cursor-not-allowed')
                    : 'hover:bg-violet-800 bg-violet-900 text-white'
                  }`}
              >
                <span className="font-bold">{isSaving ? 'Saving Progress...' : 'Finish Post-Assessment Discussion'}</span>
                {isSaving
                  ? <ButtonSpinner className="w-5 h-5 ml-2" />
                  : <CheckCircleIcon className="w-5 h-5 ml-2" aria-hidden="true" />}
              </button>
            )}
          </div>

          {/* Section Title — mirrors 1_REV.js: "{sectionNumber}. {sectionTitle}".
              The contract-type badge is highest-priority here: clinicians most
              often need the referral project during the next-steps discussion.
              It is right-aligned opposite the heading via justify-between. */}
          <div className="flex justify-between items-center gap-3 mb-6">
            <h2 className="text-2xl font-bold">{displayNumber}. {currentSectionTitle}</h2>
            <PatientContractTypeBadge
              label="Contract"
              organisation={claims?.organisation}
              assessmentType={patientDataModel?.customFields?.assessmentType}
              subContractType={patientDataModel?.customFields?.subContractType}
            />
          </div>
          

          {/* Medication Decision panel — always rendered on this page since the
              page itself is only accessible for positive ADHD diagnoses. */}
          <div className="p-8 my-8 bg-gray-50 rounded-2xl border border-gray-200 text-left">
            <h3 className="text-left text-slate-700 text-xl font-semibold mb-6">
              {displayNumber}.{panelNumbers.medication} Medication Decision
            </h3>
            <div className="space-y-4">
              <DecisionCard
                question="Does patient currently want to consider ADHD medication?"
                value={decisions.wantsMedication}
                onChange={(val) => handleDecision('wantsMedication', val)}
                disabled={!canEdit}
              />
            </div>
          </div>

          {/* Workplace Needs Assessment panel — always shown; inactive for non-Lexxic
              patients with an explanatory tooltip that remains fully visible. */}
          <div className="p-8 my-8 bg-gray-50 rounded-2xl border border-gray-200 text-left">
            <h3 className="text-left text-slate-700 text-xl font-semibold mb-6">
              {displayNumber}.{panelNumbers.workplace} Workplace Needs Assessment
            </h3>
            <div className="space-y-4">
              <DecisionCard
                question="Would patient benefit from Workplace Needs Assessment?"
                value={isWorkplaceNeedsAssessmentContract ? decisions.benefitsFromWorkplaceAssessment : null}
                onChange={(val) => handleDecision('benefitsFromWorkplaceAssessment', val)}
                disabled={!canEdit || !isWorkplaceNeedsAssessmentContract}
                inactive={!isWorkplaceNeedsAssessmentContract}
                tooltip={!isWorkplaceNeedsAssessmentContract ? 'Only applicable for Lexxic contract patients.' : undefined}
              />

              {/* Animated rationale textbox — appears when the clinician selects
                  "No" for a Lexxic patient. The text is used as LLM input to
                  generate a patient-facing paragraph explaining why a WNA was
                  not recommended, included in the TOC Patient Report. */}
              <div
                className="overflow-hidden transition-all duration-500 ease-in-out"
                style={{
                  maxHeight: (isWorkplaceNeedsAssessmentContract && decisions.benefitsFromWorkplaceAssessment === false) ? '300px' : '0px',
                  opacity: (isWorkplaceNeedsAssessmentContract && decisions.benefitsFromWorkplaceAssessment === false) ? 1 : 0,
                }}
              >
                <div className="mt-4 text-left">
                  <h4 className="text-gray-700 text-sm font-semibold mb-2">
                    Please explain why a Workplace Needs Assessment is not recommended for this patient
                    <span className="text-red-600 ml-1">*</span>
                  </h4>
                  <DictationTextArea
                    value={wnaDeclinedRationale}
                    onChange={(text) => {
                      setWnaDeclinedRationale(text);
                      setHasChanges(true);
                    }}
                    onBlur={handleWnaRationaleBlur}
                    canEdit={canEdit}
                    placeholder="e.g. The patient is not currently employed or seeking employment, therefore a Workplace Needs Assessment would not be beneficial at this time..."
                    ariaLabel="Rationale for declining Workplace Needs Assessment"
                    rows={4}
                    minHeight="8rem"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Autism Follow-Up panel — only shown when differentials decisions are
              disabled (TOC). Grayed out / inactive when autism is not an emerging
              differential. When active and answered "Yes", the report template key
              INCLUDE_AUTISM_FOLLOW_UP fires, inserting the boilerplate paragraph
              about a possible follow-up autism assessment. */}
          {showAutismFollowUpPanel && (
            <div className="p-8 my-8 bg-gray-50 rounded-2xl border border-gray-200 text-left">
              <h3 className="text-left text-slate-700 text-xl font-semibold mb-6">
                {displayNumber}.{panelNumbers.autismFollowUp} Autism Follow-Up
              </h3>
              <div className="space-y-4">
                <DecisionCard
                  question="Include autism follow-up instructions in the report?"
                  value={isAutismFollowUpActive ? decisions.includeAutismFollowUp : null}
                  onChange={(val) => handleDecision('includeAutismFollowUp', val)}
                  disabled={!canEdit || !isAutismFollowUpActive}
                  inactive={!isAutismFollowUpActive}
                  tooltip={!isAutismFollowUpActive
                    ? 'This only applies when autism is selected as an emerging differential.'
                    : 'When set to Yes, the report will include a boilerplate paragraph advising the patient to seek a follow-up autism assessment.'}
                />
              </div>
            </div>
          )}

          {/* Care Needs Assessment — always shown. This page is positive-ADHD
              only, and the care-needs paragraph lives only in that report
              variant. Yes inserts the static local-council paragraph; No
              omits it. There is no free-text rationale on No. */}
          <div className="p-8 my-8 bg-gray-50 rounded-2xl border border-gray-200 text-left">
            <h3 className="text-left text-slate-700 text-xl font-semibold mb-6">
              {displayNumber}.{panelNumbers.careNeeds} Care Needs Assessment
            </h3>
            <div className="space-y-4">
              <DecisionCard
                question="Would the patient benefit from a care needs assessment?"
                value={decisions.includeCareNeedsAssessment}
                onChange={(val) => handleDecision('includeCareNeedsAssessment', val)}
                disabled={!canEdit}
                tooltip="When set to Yes, the report includes a paragraph explaining how to apply for a care needs assessment from the local council."
              />
            </div>
          </div>

          {/* Disclosure panel */}
          <div className="p-8 my-8 bg-gray-50 rounded-2xl border border-gray-200 text-left">
            <h3 className="text-left text-slate-700 text-xl font-semibold mb-6">
              {displayNumber}.{panelNumbers.disclosure} Disclosure
            </h3>
            <div className="space-y-4">
              <DecisionCard
                question='Include "Sharing with Friends and Family" in report?'
                value={decisions.includeDisclosureFriendsFamily}
                onChange={(val) => handleDecision('includeDisclosureFriendsFamily', val)}
                disabled={!canEdit}
              />

              <DecisionCard
                question='Include "Sharing at Work" in report?'
                value={decisions.includeDisclosureWork}
                onChange={(val) => handleDecision('includeDisclosureWork', val)}
                disabled={!canEdit}
              />

              <DecisionCard
                question='Include "Sharing in Education" in report?'
                value={decisions.includeDisclosureEducation}
                onChange={(val) => handleDecision('includeDisclosureEducation', val)}
                disabled={!canEdit}
              />
            </div>
          </div>

          {/* Lifestyle Recommendations panel */}
          <div className="p-8 my-8 bg-gray-50 rounded-2xl border border-gray-200 text-left">
            <h3 className="text-left text-slate-700 text-xl font-semibold mb-6">
              {displayNumber}.{panelNumbers.lifestyle} Lifestyle Recommendations
            </h3>
            <div className="space-y-4">
              <DecisionCard
                question='Include "Exercise and Diet" in report?'
                value={decisions.includeExerciseDiet}
                onChange={(val) => handleDecision('includeExerciseDiet', val)}
                disabled={!canEdit}
              />

              <DecisionCard
                question='Include "Relationships and Partners" in report?'
                value={decisions.includeRelationships}
                onChange={(val) => handleDecision('includeRelationships', val)}
                disabled={!canEdit}
              />

              <DecisionCard
                question='Include "Driving" in report?'
                value={decisions.includeDriving}
                onChange={(val) => handleDecision('includeDriving', val)}
                disabled={!canEdit}
              />
            </div>
          </div>

          {/* Appendix Inclusions panel — content that appears as standalone appendix
              sections in the report. Pre-selected based on the patient's intake form
              data where applicable; can be overridden by the clinician. */}
          <div className="p-8 my-8 bg-gray-50 rounded-2xl border border-gray-200 text-left">
            <h3 className="text-left text-slate-700 text-xl font-semibold mb-6">
              {displayNumber}.{panelNumbers.appendix} "Further Information and Resources" Inclusions
            </h3>
            <div className="space-y-4">
              <DecisionCard
                question='Include "Females with ADHD" in report?'
                value={decisions.includeFemalesWithADHD}
                onChange={(val) => handleDecision('includeFemalesWithADHD', val)}
                disabled={!canEdit}
                subtitleHint={patientSexAtBirth ? `Patient's sex assigned at birth: ${patientSexAtBirth}` : null}
                onSourceClick={sexAtBirthRawPrefillData.length > 0 ? () => setIsSexDialogOpen(true) : undefined}
              />

              <DecisionCard
                question='Include "Educational Advice and Accommodations" in report?'
                value={decisions.includeEducationalAdvice}
                onChange={(val) => handleDecision('includeEducationalAdvice', val)}
                disabled={!canEdit}
              />

              <DecisionCard
                question='Include "Employment Advice" in report?'
                value={decisions.includeEmploymentAdvice}
                onChange={(val) => handleDecision('includeEmploymentAdvice', val)}
                disabled={!canEdit}
              />
            </div>
          </div>

          {/* Navigation Buttons — bottom of page, duplicating the top bar so the
              "Finish" CTA is always accessible without scrolling back up. */}
          <div className="flex justify-between items-center mt-8 pt-4 border-t border-gray-200">
            <button
              onClick={handleBack}
              disabled={isNavigating}
              className="flex items-center py-2 px-4 mb-2 rounded-md shadow-md text-gray-900 border border-gray-300 transition-all duration-300 hover:bg-gray-50 disabled:opacity-60 disabled:cursor-not-allowed"
              aria-label="Back to Previous Section"
            >
              <ChevronLeftIcon className="w-5 h-5 mr-2" aria-hidden="true" />
              <span className="font-bold">{isNavigating ? 'Loading...' : `Back to ${prevSectionTitle}`}</span>
            </button>

            {canEdit && (
              <button
                onClick={handleFinish}
                disabled={!isFormComplete || isSaving || isNavigating}
                className={`flex items-center py-2 px-4 mb-2 rounded-md shadow-md transition duration-100 ml-auto
                  ${(!isFormComplete || isSaving || isNavigating)
                    ? (isSaving ? 'opacity-60 cursor-not-allowed bg-violet-900 text-white' : 'bg-gray-300 text-gray-500 cursor-not-allowed')
                    : 'hover:bg-violet-800 bg-violet-900 text-white'
                  }`}
              >
                <span className="font-bold">{isSaving ? 'Saving Progress...' : 'Finish Post-Assessment Discussion'}</span>
                {isSaving
                  ? <ButtonSpinner className="w-5 h-5 ml-2" />
                  : <CheckCircleIcon className="w-5 h-5 ml-2" aria-hidden="true" />}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Source-data dialog for "Sex assigned at birth" — uses the identical
          Dialog layout, item cards, option grid, and FormattedAnswer rendering
          as the goals-panel magnifying glass in 1_REV.js and CIQuestionPanel.js
          so the UX is consistent across the entire app. */}
      <Dialog.Root open={isSexDialogOpen} onOpenChange={setIsSexDialogOpen}>
        <Dialog.Portal>
          <Dialog.Overlay className="radix-dialog-overlay-motion fixed inset-0 bg-black/50" />
          <Dialog.Content className="radix-dialog-content-centered-motion fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-white p-6 rounded-lg shadow-lg w-[700px] max-h-[65vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <Dialog.Title className="text-base font-semibold text-gray-900">
                Patient Form Responses
              </Dialog.Title>
              <Dialog.Close asChild>
                <button className="p-1 rounded-full hover:bg-gray-100">
                  <XIcon className="h-5 w-5 text-gray-500" />
                </button>
              </Dialog.Close>
            </div>

            <div className="mb-4">
              <h3 className="font-semibold text-sm mb-2">Sex Assigned at Birth</h3>

              <div className="space-y-2 mt-2">
                {sexAtBirthRawPrefillData.length > 0 ? (
                  sexAtBirthRawPrefillData.map((item, i) => (
                    <div key={i} className={`border rounded-md p-3 ${
                      item.hasAnswer
                        ? 'bg-violet-50 shadow-sm border-violet-200'
                        : 'bg-gray-50 border-dashed border-gray-200 opacity-75'
                    }`}>
                      <div className="text-xs text-gray-500 mb-0.5">From: {item.formName}</div>

                      {item.questionTitle && (
                        <div className="text-xs font-medium text-gray-700 mb-1">{item.questionTitle}</div>
                      )}

                      <div className="text-xs font-medium mb-1.5">{item.question}</div>

                      <div className={`p-2 rounded border whitespace-pre-wrap text-xs ${
                        item.hasAnswer
                          ? 'bg-white border-gray-200'
                          : 'bg-gray-100 border-gray-200 border-dashed text-gray-500 italic'
                      }`}>
                        <FormattedAnswer
                          rawAnswer={item.rawAnswer}
                          displayText={item.answer}
                        />
                      </div>

                      {item.options && item.options.length > 0 && (
                        <div className="mt-2">
                          <div className="text-xs text-gray-500 mb-1">Available options:</div>
                          <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5">
                            {item.options.map((option, optIndex) => {
                              const selected = item.hasAnswer && isOptionSelected(option, item.rawAnswer);
                              return (
                                <div
                                  key={optIndex}
                                  className={`text-xs p-1 rounded ${
                                    selected
                                      ? 'bg-violet-100 border border-violet-300 text-violet-700 font-medium'
                                      : 'bg-gray-50 border border-gray-200 text-gray-500'
                                  }`}
                                >
                                  {option}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  ))
                ) : (
                  <p className="text-gray-500 italic">No source data available</p>
                )}
              </div>
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>

      <ModuleCompletionDialog
        open={isDialogOpen && !isGenerating}
        onOpenChange={setIsDialogOpen}
        title="Post-Assessment Discussion Complete"
        width={600}
        actions={
          <>
            <CancelButton onClick={() => setIsDialogOpen(false)} />
            <GenerateReportButton onClick={handleGenerateReport} />
          </>
        }
      >
        <SummarySection title="Summary:">
          The Post-Assessment Discussion has been completed. Would you like to generate the report?
        </SummarySection>
      </ModuleCompletionDialog>

      <GenerateReportProgressDialog
        reportStage="Post-Assessment Discussion"
        patientId={patientId}
        clinicianId={auth.currentUser?.uid || ''}
        handleNextPageButtonClick={handleNextPageButtonClick}
        isGenerating={isGenerating}
        onCancel={handleCancelGeneration}
      />
    </Layout>
  );
}


/**
 * Checks whether a given option string appears in the patient's raw answer.
 * Handles the various answer shapes that come back from the intake form:
 * plain string, array of strings, array of {value} objects, or a single
 * {value} object.  Identical to the helper in 1_REV.js / CIQuestionPanel.js
 * to keep behaviour consistent across the app.
 */
function isOptionSelected(option, rawAnswer) {
  if (!rawAnswer) return false;
  if (typeof rawAnswer === 'string') return rawAnswer === option;
  if (Array.isArray(rawAnswer) && typeof rawAnswer[0] === 'string') return rawAnswer.includes(option);
  if (Array.isArray(rawAnswer) && typeof rawAnswer[0] === 'object') return rawAnswer.some(a => a.value === option);
  if (rawAnswer && typeof rawAnswer === 'object' && 'value' in rawAnswer) return rawAnswer.value === option;
  return false;
}


/**
 * DecisionCard — single Yes/No row matching DIS/TRT horizontal options:
 * label in a fixed-width column, options in ml-5 with fixed w-64 pills.
 *
 * Pill buttons use w-64 shrink-0 (no flex-1 on the options column or on each
 * <li>) so they stay the same width as TRT/DIS.
 *
 * Props:
 *  - tooltip: optional text shown via InfoTooltip next to the question
 *  - inactive: dims the question label and option pills for inapplicable
 *              questions (e.g. non-Lexxic). The InfoTooltip is deliberately
 *              hoisted OUTSIDE the dimmed container so it stays fully visible
 *              and clickable even when the rest of the row is at low opacity.
 *              CSS opacity is inherited by all descendants and cannot be
 *              "overridden" on a child, so the only way to exempt the tooltip
 *              is to place it outside the opacity wrapper entirely.
 */
/**
 * DecisionCard — renders a Yes/No toggle row for a single report-inclusion
 * decision.
 *
 * @param {string}   question      - The decision question text.
 * @param {boolean}  value         - Current decision value (true/false/null).
 * @param {Function} onChange      - Callback when the user selects an option.
 * @param {boolean}  disabled      - Whether the pills are disabled.
 * @param {string}   tooltip       - Optional info-tooltip content on the left.
 * @param {boolean}  inactive      - Dims the row (used for dependent questions).
 * @param {string}   hint          - Small italic text shown below the pills.
 * @param {string}   subtitleHint  - Small italic text shown directly below the
 *                                   question label (not below the pills).  Used
 *                                   when the hint is contextual to the question
 *                                   itself, e.g. "Patient's sex assigned at
 *                                   birth: Female".
 * @param {Function} onSourceClick - Optional callback rendered as a magnifying-
 *                                   glass icon next to `subtitleHint`.  Opens a
 *                                   dialog showing the raw form answer.
 */
function DecisionCard({ question, value, onChange, disabled, tooltip, inactive, hint, subtitleHint, onSourceClick }) {
  const isDisabled = disabled || inactive;

  const options = [
    { label: 'Yes', matches: true },
    { label: 'No', matches: false },
  ];

  return (
    <div className="mb-4 flex items-start">
      {/* Tooltip icon — lives outside the dim container so it is always fully
          visible and interactive even when the question row is inactive.
          A fixed-width spacer is used when there is no tooltip so alignment
          is preserved across all rows in the panel. */}
      <div className="w-5 shrink-0 mt-0.5">
        {tooltip && (
          <InfoTooltip
            content={tooltip}
            label={`Information about ${question}`}
            maxWidth="300px"
          />
        )}
      </div>

      {/* Question label + option pills — dimmed when inactive */}
      <div className={`flex items-start transition-opacity duration-200 ${inactive ? 'opacity-50' : ''}`}>
        <div className="w-[22rem] shrink-0 ml-2">
          <span className="text-left font-semibold text-gray-700">{question}</span>

          {/* Subtitle hint — rendered directly beneath the question label so the
              clinician sees the contextual info (e.g. patient's sex at birth)
              before choosing Yes/No.  An optional magnifying-glass icon opens a
              dialog with the raw source form answer. */}
          {subtitleHint && (
            <div className="flex items-center gap-1 mt-0.5">
              <p className="text-xs text-gray-400 italic">{subtitleHint}</p>
              {onSourceClick && (
                <div
                  className="cursor-pointer"
                  onClick={onSourceClick}
                  role="button"
                  tabIndex={0}
                  aria-label="View source form answer"
                  onKeyDown={(e) => e.key === 'Enter' && onSourceClick()}
                >
                  <div className="flex items-center gap-1 text-xs text-violet-800 font-medium rounded-full px-1 py-1 transition-all duration-200 hover:bg-violet-100">
                    <SearchIcon className="h-3 w-3" />
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="ml-5 shrink-0 flex flex-col gap-1.5">
          <ul className="items-center flex flex-row gap-4">
            {options.map((option) => {
              const isSelected = value === option.matches;

              return (
                <li
                  key={option.label}
                  onClick={() => !isDisabled && onChange(isSelected ? null : option.matches)}
                  className={`
                    p-2 w-64 shrink-0
                    rounded-lg transition-all duration-300 ease-in-out
                    text-center
                    flex flex-row items-center justify-center gap-2
                    ${isDisabled
                      ? 'cursor-not-allowed opacity-75'
                      : 'cursor-pointer group'
                    }
                    ${isSelected
                      ? 'bg-violet-600 border-2 border-violet-800 font-semibold shadow-lg transform scale-105'
                      : !isDisabled
                        ? 'bg-white hover:bg-gray-50 border-2 border-dashed border-gray-300 hover:border-gray-400 shadow-sm hover:shadow-md'
                        : 'bg-white border-2 border-dashed border-gray-300 shadow-sm'
                    }
                  `}
                >
                  <div className={`transition-all duration-300 ease-in-out ${isSelected ? 'text-white' : 'text-gray-400 group-hover:text-gray-600'}`}>
                    {isSelected ? (
                      <CheckIcon className="w-4 h-4" />
                    ) : (
                      <PlusIcon className="w-4 h-4" />
                    )}
                  </div>
                  <span className={`font-semibold transition-all duration-300 ease-in-out ${isSelected ? 'text-white' : 'text-gray-700 group-hover:text-gray-900'}`}>
                    {option.label}
                  </span>
                </li>
              );
            })}
          </ul>
          {/* Inline contextual hint — shown as small italic text below the pills.
              Used for informational cues that don't need a tooltip hover. */}
          {hint && (
            <p className="text-xs text-gray-400 italic ml-1">{hint}</p>
          )}
        </div>
      </div>
    </div>
  );
}
